import { createReducer } from '@reduxjs/toolkit'
import { fetchTokenList, acceptTokensUpdate } from './actions'
import { TokenList } from 'utils/getChronosTokenList'
import store from 'state'

export interface GlobalsState {
  readonly data: {
    readonly current: TokenList | null
    readonly pendingUpdate: TokenList | null
    readonly loadingRequestId: string | null
    readonly error: string | null
  }
}

type GlobalState = GlobalsState['data']

const NEW_GLOBAL_STATE: GlobalState = {
  error: null,
  current: null,
  loadingRequestId: null,
  pendingUpdate: null,
}

type Mutable<T> = { -readonly [P in keyof T]: T[P] extends ReadonlyArray<infer U> ? U[] : T[P] }

const initialState: GlobalsState = {
  data: NEW_GLOBAL_STATE,
}

export default createReducer(initialState, (builder) =>
  builder
    .addCase(fetchTokenList.pending, (state, { payload: { requestId } }) => {
      state.data = {
        ...state.data,
        loadingRequestId: requestId,
        error: null,
      }
      state.data.current = null
      state.data.pendingUpdate = null
    })
    .addCase(fetchTokenList.fulfilled, (state, { payload: { requestId, tokenList } }) => {
      const current = state.data?.current
      const loadingRequestId = state.data?.loadingRequestId

      // no-op if update does nothing
      if (current) {
        if (loadingRequestId === null || loadingRequestId === requestId) {
          state.data = {
            ...state.data,
            loadingRequestId: null,
            error: null,
            current: current,
            pendingUpdate: tokenList,
          }
        }
      } else {
        state.data = {
          ...state.data,
          loadingRequestId: null,
          error: null,
          current: tokenList,
          pendingUpdate: null,
        }
      }
    })
    .addCase(fetchTokenList.rejected, (state, { payload: { requestId, errorMessage } }) => {
      if (state.data?.loadingRequestId !== requestId) {
        // no-op since it's not the latest request
        return
      }

      state.data = {
        ...state.data,
        loadingRequestId: null,
        error: errorMessage,
        current: null,
        pendingUpdate: null,
      }
    })
    .addCase(acceptTokensUpdate, (state) => {
      if (!state.data?.pendingUpdate) {
        throw new Error('accept list update called without pending update')
      }
      state.data = {
        ...state.data,
        pendingUpdate: null,
        current: state.data.pendingUpdate,
      }
    })
)
